import type { CartProductOption } from '@/modules/cart/domain/CartProductOption';
import type { LineItem } from '@/modules/cart/infrastructure/dto/HttpCartRepositoryDto';
import { SHIPPING_METHOD_TYPE } from '@/modules/perseo-billing/constants/src/providers/programmed-shipping-engine';
import {
  constructHomeDelivery,
  constructPickupDelivery
} from '@/modules/perseo-billing/hooks/order-confirmation/delivery-utils';
import type { IUserCustomerData } from '@/modules/perseo-billing/hooks/order-confirmation/use-map-order-confirmation';
import { formatPrice } from '@/modules/perseo-billing/hooks/utils';
import { EServiceType, type IPrice } from '@/modules/perseo-billing/interfaces';
import type {
  IBasketSummary,
  TBillingOrderConfirmationProductItem as TOrderProduct,
  TOrderConfirmationDelivery
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing/index';
import type {
  IDeliveryGroup,
  IOrder
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/orderV2';
import { calculateProductPriceWithDiscounts } from '@/modules/perseo-billing/utils/calculate-discounts-amount';
import { Collections } from '@/modules/product/infrastructure/dto/HttpProductDetailsDto';
import { productOptionToService } from '@/sections/shippingMethods/mappers/shippingMethodMapper';

const lineItemToProductItem = (lineItem: LineItem): TOrderProduct => {
  const variant = lineItem.variant;
  const prices = variant?.prices;
  const regularCentAmount = prices?.regular?.value?.centAmount ?? 0;

  const newPrices: IPrice[] = [
    {
      id: prices?.regular?.id ?? '',
      price: regularCentAmount,
      priceFormat: formatPrice(regularCentAmount)
    }
  ];

  return {
    sku: variant?.sku ?? '',
    img: variant?.images?.[0]?.url ?? '',
    title: lineItem?.name?.['es-CL'] ?? '',
    quantity: lineItem?.quantity ?? 0,
    price: {
      price: String(newPrices?.[0]?.priceFormat) ?? '',
      isCencoPrice: false,
      isColabPrice: false
    },
    sellerId: variant?.sellers?.[0] ?? '',
    prices: newPrices,
    isSampling: false,
    isSecondHand: false,
    isPresale: false,
    isColabPrice: false,
    isService: false,
    serviceType: EServiceType.warranty,
    isEmailTrigger: false,
    options: [],
    duration: '',
    priceFormat: ''
  };
};

const getDeliveryDetails = (
  delivery: IDeliveryGroup,
  order: IOrder
): TOrderConfirmationDelivery => {
  const deliveryMethod = delivery?.shippingOption?.shippingType;

  const isPickUp = deliveryMethod === 'COLLECT';
  if (isPickUp) {
    const pickupDelivery = constructPickupDelivery(delivery, order);
    return pickupDelivery;
  }

  const homeDelivery = constructHomeDelivery(delivery, order);
  return homeDelivery;
};

const getIntangiblesItems = (order: IOrder, customerEmail: string) => {
  const intangibleItems = order?.lineItems?.filter((item) => {
    if (!item) return false;

    const attributes = item?.variant?.attributes ?? [];
    const departmentId = attributes?.find(
      (attr) => attr.name === 'departmentId'
    );
    const isIntangible = departmentId?.value === '500';
    return isIntangible;
  });

  const intangibleItemsToProductItem = intangibleItems.map((item) => {
    return lineItemToProductItem(item);
  });

  return intangibleItems.map((item) => {
    return {
      soldBy: item?.variant?.sellers?.[0],
      sendBy: '',
      deliveryDetails: {
        shippingMethodType: SHIPPING_METHOD_TYPE.INTANGIBLE,
        customerEmail: customerEmail
      },
      items: intangibleItemsToProductItem,
      timeStamp: order?.payments?.[0]?.transactions?.[0]?.timestamp || ''
    };
  });
};

const getWeddingTangibleItems = (order: IOrder, email: string) => {
  const tangibleItems = order?.lineItems?.filter((item) => {
    if (!item) return false;

    const attributes = item?.variant?.attributes ?? [];
    const departmentId = attributes?.find(
      (attr) => attr.name === 'departmentId'
    );
    const isTangible = departmentId?.value !== '500';
    return isTangible;
  });

  const tangibleItemsToProductItem = tangibleItems?.map((item) => {
    return lineItemToProductItem(item);
  });

  return tangibleItems?.map((item) => {
    return {
      soldBy: item?.variant?.sellers?.[0],
      sendBy: '',
      deliveryDetails: {
        shippingMethodType: SHIPPING_METHOD_TYPE.NOVIOS,
        customerEmail: email
      },
      items: tangibleItemsToProductItem,
      timeStamp: order?.payments?.[0]?.transactions?.[0]?.timestamp || ''
    };
  });
};

const getShippingBasketSummary = (
  order: IOrder,
  customer: IUserCustomerData
): IBasketSummary[] => {
  const { email, isColabCustomer } = customer;

  const shippingBasketSummary: IBasketSummary[] =
    order?.deliveryInfo?.deliveryGroups?.map((delivery) => {
      const items = delivery?.products?.map((product) => {
        return order?.lineItems?.find((item) => {
          return item.variant.sku === product;
        });
      });

      const itemsToReturn =
        items?.map((product) => {
          const {
            custom,
            variant,
            discountedPricePerQuantity = []
          } = product || {};
          const { offer, paymentMethod, regular } = variant?.prices || {};
          const isCencoPrice = !offer && !!paymentMethod;
          const appliedPrice = offer || paymentMethod || regular;
          const priceId = appliedPrice?.id;
          const priceCentAmount = appliedPrice?.value?.centAmount ?? 0;
          const priceWithDiscounts = calculateProductPriceWithDiscounts(
            priceCentAmount,
            discountedPricePerQuantity
          );
          const services = order.productOptions
            .filter(
              (productOption) =>
                productOption.productSku === product?.productKey
            )
            .map((service) => {
              const serviceFormatted = {
                ...service,
                name: service?.name?.['es-CL'] ?? ''
              };
              return productOptionToService(
                serviceFormatted as unknown as CartProductOption
              );
            });

          const isColabPrice =
            isColabCustomer &&
            !!variant?.collections?.includes(
              Collections.TAG_BENEFICIOS_CRUZADOS_COLABORADORES
            );

          // variant prices are 3 objects, we need to map them to the IPrice interface
          const newPrices: IPrice[] = [
            {
              id: priceId ?? '',
              price: priceCentAmount,
              priceFormat: formatPrice(priceWithDiscounts)
            }
          ];

          const productTitle = product?.name?.['es-CL'] ?? '';

          return {
            sku: variant?.sku ?? '',
            img: variant?.images?.[0]?.url ?? '',
            title: productTitle,
            quantity: product?.quantity ?? 0,
            price: {
              price: String(newPrices?.[0]?.priceFormat) ?? '',
              currency: order?.totalPrice?.currencyCode ?? '',
              isCencoPrice,
              isColabPrice
            },
            sellerId: variant?.sellers?.[0] || '',
            prices: newPrices,
            isSampling: false,
            isSecondHand: false,
            isPresale: false,
            isColabPrice,
            isService: !!services?.length,
            serviceType: EServiceType.warranty,
            isEmailTrigger: false,
            options: [],
            duration: '',
            priceFormat: '',
            productKey: product?.productKey,
            custom,
            services
          };
        }) || [];

      const shippingOption = delivery?.shippingOption;
      const soldBy = items?.[0]?.variant?.sellers?.[0] ?? '';
      return {
        soldBy: soldBy,
        sendBy: shippingOption?.originNodeAdmin ?? '',
        deliveryDetails: getDeliveryDetails(delivery, order),
        items: itemsToReturn,
        timeStamp: order?.payments?.[0]?.transactions?.[0]?.timestamp
      };
    }) || [];

  const intangibleToBasketSummary = getIntangiblesItems(order, email);

  shippingBasketSummary.push(...intangibleToBasketSummary);

  if (shippingBasketSummary?.length === 0) {
    return getWeddingTangibleItems(order, email);
  }

  return shippingBasketSummary;
};

export default getShippingBasketSummary;
